import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Lenis from '@studio-freight/lenis';
import Layout from '../components/Layout';
import PunchInOutWidget from '../components/PunchInOutWidget';
import useAuthStore from '../store/authStore';
import api from '../services/api';
import './HRDashboard.css';

const HRDashboard = () => {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const [stats, setStats] = useState({
    totalEmployees: 0,
    activeEmployees: 0,
    presentToday: 0,
    onLeave: 0,
    absentToday: 0,
    departmentStats: []
  });
  const [employees, setEmployees] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);


  useEffect(() => {
    loadDashboard();

    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smooth: true
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);



  const loadDashboard = async () => {
    try {
      const [statsRes, employeesRes, attendanceRes] = await Promise.all([
        api.get('/hr/dashboard/stats'),
        api.get('/hr/dashboard/employees'),
        api.get('/hr/dashboard/attendance')
      ]);

      setStats(statsRes.data.data);
      setEmployees(employeesRes.data.data);
      setAttendance(attendanceRes.data.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };



  const getRoleBadgeColor = (role) => {
    const colors = {
      'HR': 'purple',
      'Operations Head': 'blue',
      'Project Manager': 'green',
      'Project Lead': 'orange',
      'Developer': 'cyan',
      'QA Engineer': 'pink',
      'UI Engineer': 'indigo'
    };
    return colors[role] || 'gray';
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDuration = (minutes) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h}h ${m}m`;
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="hr-dashboard">
        <div className="dashboard-header">
          <div>
            <h1 className="dashboard-title">HR Dashboard</h1>
            <p className="dashboard-subtitle">Welcome back, {user?.name}</p>
          </div>
          <button className="btn-primary" onClick={() => navigate('/team/add', { state: { from: 'dashboard' } })}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Add Employee
          </button>
        </div>

        <PunchInOutWidget />

        <div className="stats-grid">
          <div className="stat-card primary">
            <svg className="stat-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 20c0-1.657-2.239-3-5-3s-5 1.343-5 3M17 20H7m5-7a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
            </svg>
            <div className="stat-content">
              <div className="stat-value">{stats.totalEmployees}</div>
              <div className="stat-label">TOTAL EMPLOYEES</div>
            </div>
          </div>

          <div className="stat-card success">
            <svg className="stat-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
            </svg>
            <div className="stat-content">
              <div className="stat-value">{stats.activeEmployees}</div>
              <div className="stat-label">ACTIVE EMPLOYEES</div>
            </div>
          </div>

          <div className="stat-card info">
            <svg className="stat-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
            </svg>
            <div className="stat-content">
              <div className="stat-value">{stats.presentToday}</div>
              <div className="stat-label">PRESENT TODAY</div>
            </div>
          </div>

          <div className="stat-card warning">
            <svg className="stat-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
            </svg>
            <div className="stat-content">
              <div className="stat-value">{stats.onLeave}</div>
              <div className="stat-label">ON LEAVE</div>
            </div>
          </div>

          <div className="stat-card danger">
            <svg className="stat-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="m5.636 5.636 12.728 12.728M5.636 18.364 18.364 5.636M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
            </svg>
            <div className="stat-content">
              <div className="stat-value">{stats.absentToday}</div>
              <div className="stat-label">ABSENT / NOT PUNCHED-IN</div>
            </div>
          </div>
        </div>

        <div className="dashboard-card full-width">
          <div className="card-header">
            <h2 className="card-title">Today's Attendance</h2>
            <span className="card-badge">{attendance.length} Present</span>
          </div>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Role</th>
                  <th>Punch In</th>
                  <th>Punch Out</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {attendance.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="empty-table-cell">No attendance records today</td>
                  </tr>
                ) : (
                  attendance.slice(0, 10).map((record) => (
                    <tr key={record._id}>
                      <td>
                        <div className="employee-cell">
                          <div className="employee-avatar">
                            {record.user?.name?.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="employee-name">{record.user?.name}</div>
                            <div className="text-secondary" style={{ fontSize: '12px' }}>{record.user?.email}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`role-badge badge-${getRoleBadgeColor(record.user?.role)}`}>
                          {record.user?.role}
                        </span>
                      </td>
                      <td>
                        <div className="time-cell in">
                          {formatTime(record.punchInTime)}
                        </div>
                      </td>
                      <td>
                        {record.punchOutTime ? (
                          <div className="time-cell out">
                            {formatTime(record.punchOutTime)}
                          </div>
                        ) : (
                          <span className="text-secondary">—</span>
                        )}
                      </td>
                      <td>
                        {record.isLatePunchIn ? (
                          <span className={`status-tag ${record.status === 'Half Day' ? 'danger' : 'warning'}`}>
                            {record.status === 'Half Day' ? 'Half Day' : `Late ${formatDuration(record.lateMinutes)}`}
                          </span>
                        ) : (
                          <span className="status-tag success">On Time</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="dashboard-card full-width">
          <div className="card-header">
            <h2 className="card-title">Department Overview</h2>
          </div>
          <div className="department-list">
            {stats.departmentStats.map((dept) => (
              <div key={dept._id} className="department-item" data-role={dept._id}>
                <div className="department-info">
                  <div className="department-count">{dept.count}</div>
                  <span className={`role-badge badge-${getRoleBadgeColor(dept._id)}`}>
                    {dept._id}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="dashboard-card full-width">
          <div className="card-header">
            <h2 className="card-title">Recent Employees</h2>
            <button className="btn-link" onClick={() => navigate('/team')}>
              View All →
            </button>
          </div>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Role</th>
                  <th>Email</th>
                  <th>Mobile</th>
                  <th>Reports To</th>
                  <th>Joined</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((emp) => (
                  <tr key={emp._id}>
                    <td>
                      <div className="employee-cell">
                        <div className="employee-avatar">
                          {emp.name.charAt(0).toUpperCase()}
                        </div>
                        <span className="employee-name">{emp.name}</span>
                      </div>
                    </td>
                    <td>
                      <span className={`role-badge badge-${getRoleBadgeColor(emp.role)}`}>
                        {emp.role}
                      </span>
                    </td>
                    <td className="text-secondary">{emp.email}</td>
                    <td className="text-secondary">{emp.mobile}</td>
                    <td className="text-secondary">
                      {emp.reportsTo ? emp.reportsTo.name : '—'}
                    </td>
                    <td className="text-secondary">{formatDate(emp.createdAt)}</td>
                    <td>
                      <span className={`status-badge ${emp.isActive ? 'active' : 'inactive'}`}>
                        {emp.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="quick-actions">
          <h2 className="section-title">Quick Actions</h2>
          <div className="actions-grid">
            <button className="action-card" onClick={() => navigate('/team')}>
              <div className="action-title">Manage Employees</div>
              <div className="action-desc">Add, edit, or remove employees</div>
            </button>
            <button className="action-card" onClick={() => navigate('/hr/attendance')}>
              <div className="action-title">Attendance</div>
              <div className="action-desc">Track & manage attendance</div>
            </button>
            <button className="action-card" onClick={() => navigate('/reports')}>
              <div className="action-title">View Reports</div>
              <div className="action-desc">Attendance & employee reports</div>
            </button>
          </div>
        </div>


      </div>
    </Layout>
  );
};

export default HRDashboard;
